/**
 * MasterDataPackageFormDialog — Create/Edit `package` theo Access
 * `frmMdQryPackageAddver2` (song song với Certification ver2).
 *
 * prem_discount + nw_per_bag_kg KHÔNG lưu ở bảng package mà là LỊCH SỬ nhiều bản
 * ghi trong package_change (mỗi bản = date_change + prem_discount + nw_per_bag_kg).
 * Dialog gồm:
 *   - Header: Packaging Code (PK, khóa sau khi tạo) + Packaging + Description Eng/Vn
 *   - Bảng lịch sử (Changed date, Prem discount, NW/bag) — read từ API
 *   - Ô nhập Date + Prem discount + NW/bag + nút Add / Delete
 * Nút Save (title) = tạo/cập nhật header + đóng.
 *
 * UI theo /convert-form: Save/Cancel ở DialogTitle (dialogHeaderBtnSx), DialogContent
 * compact, field khóa dùng roSx, number type=text format-on-blur, date DatePicker
 * DD-MMM-YYYY. Enhancement (user-request "wow"): avatar + stat cards + badge LATEST +
 * highlight dòng chọn — chỉ trình bày.
 */
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useAddMdPackageChangeMutation,
  useCreateMdPackageMutation,
  useDeleteMdPackageChangeMutation,
  useLazyCheckMdPackageCodeQuery,
  useLazySuggestMdPackageCodeQuery,
  useListMdPackageChangesQuery,
  useUpdateMdPackageMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataPackage } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  numSx,
  roSx,
  sectionHeaderSx,
} from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataPackage | null
  onClose: () => void
  onSaved?: () => void
}

const parseNumeric = (s: string): number | null => {
  const t = (s ?? '').replace(/,/g, '').trim()
  if (t === '') return null
  const n = Number(t)
  return Number.isNaN(n) ? null : n
}
// prem_discount + nw_per_bag_kg là number (Standard #,##0.00 — như certification.premium).
const formatStandard = (v: string | number | null | undefined): string => {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''))
  if (Number.isNaN(n)) return String(v)
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const formatDate = (v: unknown): string => {
  if (!v) return ''
  const d = dayjs(String(v))
  return d.isValid() ? d.format('DD-MMM-YYYY') : String(v)
}
const dayKey = (v: unknown): string => {
  const d = dayjs(String(v))
  return d.isValid() ? d.format('YYYY-MM-DD') : ''
}

export default function MasterDataPackageFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createPackage, { isLoading: creating }] = useCreateMdPackageMutation()
  const [updatePackage, { isLoading: updating }] = useUpdateMdPackageMutation()
  const [addChange, { isLoading: addingChange }] = useAddMdPackageChangeMutation()
  const [deleteChange, { isLoading: deletingChange }] = useDeleteMdPackageChangeMutation()
  const [suggestCode] = useLazySuggestMdPackageCodeQuery()
  const [checkCode] = useLazyCheckMdPackageCodeQuery()
  const [codeDup, setCodeDup] = useState(false)

  // Header fields.
  const [code, setCode] = useState('')
  const [packaging, setPackaging] = useState('')
  const [descEng, setDescEng] = useState('')
  const [descVn, setDescVn] = useState('')
  const [headerCreated, setHeaderCreated] = useState(false)
  // Ô nhập 1 bản ghi change.
  const [dateChange, setDateChange] = useState('') // YYYY-MM-DD
  const [premInput, setPremInput] = useState('')
  const [nwInput, setNwInput] = useState('')
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    setCodeDup(false)
    setDateChange('')
    setPremInput('')
    setNwInput('')
    if (isEdit && initialValue) {
      setCode(initialValue.packaging_code ?? '')
      setPackaging(initialValue.packaging ?? '')
      setDescEng(initialValue.description_eng ?? '')
      setDescVn(initialValue.description_vn ?? '')
      setHeaderCreated(true)
    } else {
      setCode('')
      setPackaging('')
      setDescEng('')
      setDescVn('')
      setHeaderCreated(false)
      // Enhancement: gợi ý mã kế tiếp (PACK<nn>), pre-fill nhưng vẫn cho sửa.
      suggestCode()
        .unwrap()
        .then((r) => setCode((prev) => (prev ? prev : r.packaging_code || '')))
        .catch(() => undefined)
    }
  }, [open, isEdit, initialValue, suggestCode])

  const effectiveCode = headerCreated ? code.trim() : ''
  const { data: changesData, isFetching: loadingChanges } = useListMdPackageChangesQuery(
    effectiveCode,
    { skip: !effectiveCode },
  )
  const changes = useMemo(() => changesData?.items ?? [], [changesData])

  // Bản ghi mới nhất (theo ngày) → stat + badge LATEST.
  const latest = useMemo(() => {
    let best: (typeof changes)[number] | null = null
    for (const r of changes) {
      if (!r.changed_date) continue
      if (!best || dayjs(String(r.changed_date)).isAfter(dayjs(String(best.changed_date)))) best = r
    }
    return best
  }, [changes])

  const codeEmpty = !code.trim()
  const busy = creating || updating || addingChange || deletingChange
  const codeLocked = isEdit || headerCreated

  const handleClose = () => {
    if (busy) return
    onClose()
  }

  const headerBody = () => ({
    packaging: packaging.trim() || null,
    description_eng: descEng.trim() || null,
    description_vn: descVn.trim() || null,
  })

  /** Đảm bảo header package tồn tại (tạo nếu New chưa tạo). */
  const ensureHeader = async (): Promise<string | null> => {
    const c = code.trim()
    if (!c) {
      setShowErrors(true)
      return null
    }
    if (codeDup) {
      snack.warning('This packaging code already exists.')
      return null
    }
    if (!headerCreated) {
      await createPackage({ packaging_code: c, ...headerBody() }).unwrap()
      setHeaderCreated(true)
    }
    return c
  }

  const handleSaveChange = async () => {
    if (codeEmpty || !dateChange) {
      setShowErrors(true)
      return
    }
    try {
      const c = await ensureHeader()
      if (!c) return
      await addChange({
        packagingCode: c,
        body: { date_change: dateChange, prem_discount: parseNumeric(premInput), nw_per_bag_kg: parseNumeric(nwInput) },
      }).unwrap()
      snack.success('Package change added')
      setDateChange('')
      setPremInput('')
      setNwInput('')
      onSaved?.()
    } catch (e) {
      snack.error(getErrorMessage(e, 'Failed to add package change'))
    }
  }

  const handleDeleteChange = async () => {
    const c = code.trim()
    if (!c || !dateChange) {
      snack.warning('Please choose a change record to delete!')
      return
    }
    try {
      await deleteChange({ packagingCode: c, dateChange }).unwrap()
      snack.success('Package change deleted')
      setDateChange('')
      setPremInput('')
      setNwInput('')
      onSaved?.()
    } catch (e) {
      snack.error(getErrorMessage(e, 'Failed to delete package change'))
    }
  }

  const handleSave = async () => {
    if (codeEmpty) {
      setShowErrors(true)
      return
    }
    try {
      const justCreated = !headerCreated
      const c = await ensureHeader()
      if (!c) return
      // Header đã tồn tại từ trước (edit, hoặc new đã auto-tạo khi add change) →
      // cập nhật lại field header. Vừa create thì create đã ghi đủ, khỏi update.
      if (!justCreated) {
        await updatePackage({ code: c, body: headerBody() }).unwrap()
      }
      snack.success(isEdit ? 'Package updated' : 'Package saved')
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  const selectRow = (r: (typeof changes)[number]) => {
    if (!r.changed_date) return
    setDateChange(dayKey(r.changed_date))
    setPremInput(formatStandard(r.prem_discount))
    setNwInput(formatStandard(r.nw_per_bag_kg))
  }

  const selectedKey = dateChange
  const editingExisting = !!selectedKey && changes.some((r) => dayKey(r.changed_date) === selectedKey)

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (busy) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Avatar variant="rounded" sx={{ width: 30, height: 30, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
          <Inventory2RoundedIcon sx={{ fontSize: '1.05rem' }} />
        </Avatar>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Package' : 'New Package'}
          {code ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {code}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={codeEmpty || codeDup || busy}
            sx={dialogHeaderBtnSx}
          >
            {busy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={busy} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {/* ── Header fields ─────────────────────────────────────────── */}
        <Grid container spacing={1.25} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Packaging Code"
              size="small"
              fullWidth
              required
              disabled={codeLocked}
              error={(showErrors && codeEmpty) || codeDup}
              value={code}
              onChange={(e) => { setCode(e.target.value); setCodeDup(false) }}
              onBlur={() => {
                const c = code.trim()
                if (codeLocked || !c) return
                void checkCode(c).unwrap().then((r) => setCodeDup(!!r.exists)).catch(() => undefined)
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: codeLocked ? -1 : 1 }}
              helperText={
                codeLocked
                  ? 'Code cannot be changed after creation.'
                  : codeDup
                    ? 'This packaging code already exists.'
                    : 'Enter code, then add change records below.'
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {codeLocked ? <LockOutlinedIcon fontSize="small" color="disabled" /> : <BadgeOutlinedIcon fontSize="small" color="action" />}
                  </InputAdornment>
                ),
              }}
              sx={codeLocked ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Packaging"
              size="small"
              fullWidth
              value={packaging}
              onChange={(e) => setPackaging(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Description Eng"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={descEng}
              onChange={(e) => setDescEng(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Description Vn"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={descVn}
              onChange={(e) => setDescVn(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 4 }}
            />
          </Grid>
        </Grid>

        {/* ── Stat cards ────────────────────────────────────────────── */}
        <Fade in={!!effectiveCode && changes.length > 0} unmountOnExit>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <StatCard
              icon={<LocalOfferOutlinedIcon sx={{ fontSize: '0.95rem' }} />}
              label="Latest prem discount"
              value={latest ? formatStandard(latest.prem_discount) || '—' : '—'}
              accent="secondary"
              emphasize
            />
            <StatCard
              icon={<ScaleOutlinedIcon sx={{ fontSize: '0.95rem' }} />}
              label="Latest NW / bag"
              value={latest ? formatStandard(latest.nw_per_bag_kg) || '—' : '—'}
              accent="info"
              emphasize
            />
            <StatCard
              icon={<HistoryRoundedIcon sx={{ fontSize: '0.95rem' }} />}
              label="Records"
              value={String(changes.length)}
              accent="primary"
            />
          </Stack>
        </Fade>

        {/* ── Change history (Access frmMdQryPackageChange subform) ─── */}
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1.75, mb: 0.5 }}>
          <HistoryRoundedIcon sx={{ fontSize: '0.95rem', color: 'primary.main' }} />
          <Typography sx={sectionHeaderSx}>Prem discount &amp; NW/bag history</Typography>
          {effectiveCode && changes.length > 0 ? (
            <Chip size="small" label={changes.length} color="primary" variant="outlined" sx={{ height: 16, fontSize: '0.62rem', fontWeight: 700 }} />
          ) : null}
        </Stack>
        <Paper variant="outlined" sx={{ borderRadius: 1.5, maxHeight: 200, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Changed date</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Prem discount</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>NW / bag</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!effectiveCode ? (
                <EmptyRow text="Save the package first to manage change history." />
              ) : loadingChanges ? (
                <TableRow><TableCell colSpan={3} align="center" sx={{ py: 2.5 }}><CircularProgress size={16} /></TableCell></TableRow>
              ) : changes.length === 0 ? (
                <EmptyRow text="No change records yet — add the first one below." />
              ) : (
                changes.map((r, i) => {
                  const key = dayKey(r.changed_date)
                  const selected = !!selectedKey && key === selectedKey
                  const isLatest = !!latest && key === dayKey(latest.changed_date)
                  return (
                    <TableRow
                      key={`${r.changed_date}-${i}`}
                      hover
                      selected={selected}
                      sx={{
                        cursor: 'pointer',
                        '& > td': { borderLeft: selected ? (t) => `3px solid ${t.palette.secondary.main}` : '3px solid transparent' },
                        '&.Mui-selected, &.Mui-selected:hover': { bgcolor: (t) => `${t.palette.secondary.light}55` },
                      }}
                      onClick={() => selectRow(r)}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <span>{formatDate(r.changed_date)}</span>
                          {isLatest ? (
                            <Chip
                              size="small"
                              label="LATEST"
                              sx={{ height: 15, fontSize: '0.56rem', fontWeight: 800, letterSpacing: 0.4, color: (t) => t.palette.success.dark, bgcolor: (t) => `${t.palette.success.light}44` }}
                            />
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: isLatest ? 700 : 500 }}>{formatStandard(r.prem_discount)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: isLatest ? 700 : 500 }}>{formatStandard(r.nw_per_bag_kg)}</TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </Paper>

        {/* ── Add / update change ───────────────────────────────────── */}
        <Typography sx={{ ...sectionHeaderSx, mt: 1.75, mb: 0.5 }}>
          {editingExisting ? 'Update change for selected date' : 'Add a change record'}
        </Typography>
        <Box
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            border: 1,
            borderColor: 'divider',
            bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fffaf3'),
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <DatePicker
              label="Date"
              format="DD-MMM-YYYY"
              value={dateChange ? dayjs(dateChange) : null}
              onChange={(d) => setDateChange(d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: showErrors && !dateChange,
                  InputLabelProps: { shrink: true },
                  inputProps: { tabIndex: 5 },
                },
              }}
            />
            <TextField
              label="Prem discount"
              size="small"
              fullWidth
              value={premInput}
              onChange={(e) => setPremInput(e.target.value)}
              onBlur={() => setPremInput(formatStandard(premInput))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 6, inputMode: 'decimal', style: { textAlign: 'right' } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><LocalOfferOutlinedIcon fontSize="small" color="action" /></InputAdornment> }}
              sx={numSx}
            />
            <TextField
              label="NW / bag"
              size="small"
              fullWidth
              value={nwInput}
              onChange={(e) => setNwInput(e.target.value)}
              onBlur={() => setNwInput(formatStandard(nwInput))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 7, inputMode: 'decimal', style: { textAlign: 'right' } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><ScaleOutlinedIcon fontSize="small" color="action" /></InputAdornment> }}
              sx={numSx}
            />
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
            <Tooltip title={editingExisting ? 'Remove the selected change record' : 'Pick a row above to delete it'}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<DeleteOutlineRoundedIcon />}
                  onClick={() => void handleDeleteChange()}
                  disabled={busy || !dateChange || !effectiveCode}
                >
                  Delete
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={() => void handleSaveChange()}
              disabled={busy || codeEmpty || codeDup || !dateChange}
            >
              {editingExisting ? 'Update change' : 'Add change'}
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

/** Stat card nhỏ (enhancement, chỉ display). */
function StatCard({
  icon,
  label,
  value,
  accent,
  emphasize,
}: {
  icon: ReactNode
  label: string
  value: string
  accent: 'primary' | 'secondary' | 'info'
  emphasize?: boolean
}) {
  return (
    <Paper variant="outlined" sx={{ flex: 1, px: 1.25, py: 0.75, borderRadius: 1.5, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: `${accent}.main`, mb: 0.25 }}>
        {icon}
        <Typography noWrap sx={{ fontSize: '0.6rem', letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 700, color: 'text.secondary' }}>
          {label}
        </Typography>
      </Stack>
      <Typography
        noWrap
        sx={{
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          fontSize: emphasize ? '1.05rem' : '0.9rem',
          color: emphasize ? `${accent}.dark` : 'text.primary',
          lineHeight: 1.25,
        }}
      >
        {value}
      </Typography>
    </Paper>
  )
}

/** Hàng trống trung tâm có chữ mờ. */
function EmptyRow({ text }: { text: string }) {
  return (
    <TableRow>
      <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary', py: 2.5, fontSize: '0.78rem' }}>
        {text}
      </TableCell>
    </TableRow>
  )
}
